import * as React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { InfoCard } from './InfoCard';
import { formatCurrency } from '@/lib/formatters';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { InfoIcon, Users, User, Calendar } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useTranslation } from 'react-i18next';

// Application types and income requirements
type ApplicationType = 'single' | 'couple';

interface IncomeRequirements {
  single: number;
  couple: number;
  childAmount: number;
}

function IncomeCalculator() {
  const { t } = useTranslation();
  
  // Application type input
  const [applicationType, setApplicationType] = React.useState<ApplicationType>('single');
  const [childrenCount, setChildrenCount] = React.useState<number>(0);
  const [paymentType, setPaymentType] = React.useState<'14x' | '12x'>('14x');
  
  // Financial inputs
  const [netSalary, setNetSalary] = React.useState<number>(2000);
  const [otherIncome, setOtherIncome] = React.useState<number>(0);
  const [savings, setSavings] = React.useState<number>(0);
  const [majorExpenses, setMajorExpenses] = React.useState<number>(0);
  
  // RWR+ requirements (updated for 2026)
  const EXPENSE_DEDUCTION = 386.43;
  
  // Income requirements based on application type
  const INCOME_REQUIREMENTS: IncomeRequirements = {
    single: 1308.39,
    couple: 2064.12,
    childAmount: 201.88
  };
  
  // Calculate the minimum required income based on application type and number of children
  const calculateRequiredIncome = (): number => {
    let baseIncome;
    
    if (applicationType === 'single') {
      baseIncome = INCOME_REQUIREMENTS.single + (childrenCount * INCOME_REQUIREMENTS.childAmount);
    } else { // couple
      baseIncome = INCOME_REQUIREMENTS.couple + (childrenCount * INCOME_REQUIREMENTS.childAmount);
    }
    
    return baseIncome;
  };

  // Calculate eligibility using the Austrian formula:
  // ((Net salary * 14) / 12) + Other stable income + (Savings/12) - (amount of Major expenses that exceeds €376.27)
  const calculateEligibleIncome = (): number => {
    // Adjust salary based on payment type (14x or 12x)
    const adjustedSalary = paymentType === '14x' 
      ? (netSalary * 14) / 12  // Convert from 14 payments to 12 months
      : netSalary;             // Already in 12-month format
    
    // Savings distributed over 12 months
    const monthlySavings = savings / 12;
    
    // Only subtract the amount that exceeds €376.27
    const expensesToDeduct = majorExpenses > EXPENSE_DEDUCTION 
      ? majorExpenses - EXPENSE_DEDUCTION 
      : 0;
    
    // Final calculation
    return adjustedSalary + otherIncome + monthlySavings - expensesToDeduct;
  };
  
  const eligibleIncome = calculateEligibleIncome();
  const requiredIncome = calculateRequiredIncome();
  const isEligible = eligibleIncome >= requiredIncome;
  
  // Input change handlers
  const handleApplicationTypeChange = (value: string) => {
    setApplicationType(value as ApplicationType);
  };
  
  const handleChildrenCountChange = (value: string) => {
    const count = parseInt(value, 10);
    if (!isNaN(count)) {
      setChildrenCount(count);
    }
  };
  
  const handlePaymentTypeChange = (value: string) => {
    setPaymentType(value as '14x' | '12x');
  };
  
  const handleNetSalaryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value === '' ? 0 : parseFloat(event.target.value);
    if (!isNaN(value)) {
      setNetSalary(value);
    }
  };
  
  const handleNetSalarySliderChange = (value: number[]) => {
    setNetSalary(value[0]);
  };
  
  const handleOtherIncomeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value === '' ? 0 : parseFloat(event.target.value);
    if (!isNaN(value)) {
      setOtherIncome(value);
    }
  };
  
  const handleSavingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value === '' ? 0 : parseFloat(event.target.value);
    if (!isNaN(value)) {
      setSavings(value);
    }
  };
  
  const handleMajorExpensesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value === '' ? 0 : parseFloat(event.target.value);
    if (!isNaN(value)) {
      setMajorExpenses(value);
    }
  };

  return (
    <div className="space-y-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold mb-2 text-[#006b69]">{t('calculator.title')}</h1>
        <p className="text-muted-foreground mb-6 subtitle">
          {t('calculator.subtitle')}
        </p>
        
        <Card>
          <CardHeader className="border-b border-[#ccd77b]">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[#006b69]">{t('calculator.incomeCalculatorTitle')}</CardTitle>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-[#006b69] hover:bg-[#eaeecd]">
                    <InfoIcon className="h-4 w-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="border-[#006b69]">
                  <DialogHeader>
                    <DialogTitle className="text-[#006b69]">{t('dialog.formulaTitle')}</DialogTitle>
                    <DialogDescription>
                      <p className="mt-2">{t('dialog.formulaDescription')}</p>
                      <div className="bg-[#f7f8eb] p-3 rounded-md mt-2 font-mono text-sm whitespace-normal border border-[#ccd77b]/50">
                        {paymentType === '14x' ? 
                          t('dialog.formula14x') :
                          t('dialog.formula12x')}
                      </div>
                      <p className="mt-2">{t('dialog.thresholdDescription')}</p>
                      <ul className="mt-2 space-y-1 text-sm">
                        <li>• {t('dialog.singleRequirement', { amount: formatCurrency(INCOME_REQUIREMENTS.single) })}</li>
                        <li>• {t('dialog.coupleRequirement', { amount: formatCurrency(INCOME_REQUIREMENTS.couple) })}</li>
                        <li>• {t('dialog.childRequirement', { amount: formatCurrency(INCOME_REQUIREMENTS.childAmount) })}</li>
                      </ul>
                    </DialogDescription>
                  </DialogHeader>
                </DialogContent>
              </Dialog>
            </div>
            <CardDescription>
              {t('calculator.enterDetails')}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-6">
              {/* Application Type Selection */}
              <div className="bg-[#f7f8eb] p-4 rounded-lg border border-[#ccd77b]/30">
                <h3 className="font-medium mb-3 text-[#006b69] section-header">{t('household.title')}</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="applicationType">{t('household.applicationType')}</Label>
                    <Select
                      value={applicationType}
                      onValueChange={handleApplicationTypeChange}
                    >
                      <SelectTrigger id="applicationType">
                        <SelectValue placeholder={t('household.selectApplicationType')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            <span>{t('household.single')}</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="couple">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            <span>{t('household.couple')}</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="childrenCount">{t('household.childrenCount')}</Label>
                    <Select
                      value={childrenCount.toString()}
                      onValueChange={handleChildrenCountChange}
                    >
                      <SelectTrigger id="childrenCount">
                        <SelectValue placeholder={t('household.selectChildrenCount')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">{t('household.noChildren')}</SelectItem>
                        <SelectItem value="1">{t('household.oneChild')}</SelectItem>
                        <SelectItem value="2">{t('household.twoChildren')}</SelectItem>
                        <SelectItem value="3">{t('household.threeChildren')}</SelectItem>
                        <SelectItem value="4">{t('household.fourChildren')}</SelectItem>
                        <SelectItem value="5">{t('household.fiveChildren')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="mt-2 text-xs text-muted-foreground">
                  {t('household.requiredIncomePrefix')} {formatCurrency(requiredIncome)}
                  {childrenCount > 0 && (
                    <span>
                      {' '}{t('household.includesPrefix')}{' '}
                      {childrenCount}{' '}
                      {childrenCount === 1 ? t('household.child') : t('household.children')}
                      {t('household.includesSuffix')}
                    </span>
                  )}
                </div>
              </div>
              
              <Separator className="my-3" />
              {/* Net Monthly Income */}
              <div className="grid gap-2">
                <div className="flex justify-between">
                  <Label htmlFor="netSalary">{t('income.netSalaryLabel')}</Label>
                  <div className="text-right">
                    <span className="text-sm font-medium">€ {formatCurrency(netSalary)}</span>
                  </div>
                </div>
                <div className="flex gap-4 items-center">
                  <Input
                    id="netSalary"
                    type="number"
                    min={0}
                    value={netSalary === 0 ? '' : netSalary}
                    onChange={handleNetSalaryChange}
                    className="w-24"
                  />
                  <Slider
                    value={[netSalary]}
                    min={0}
                    max={5000}
                    step={50}
                    onValueChange={handleNetSalarySliderChange}
                    className="flex-1"
                  />
                </div>
                
                <div className="mt-2">
                  <Label className="text-sm mb-1.5 block">{t('income.paymentScheduleLabel')}</Label>
                  <RadioGroup 
                    value={paymentType} 
                    onValueChange={handlePaymentTypeChange}
                    className="flex flex-row gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="14x" id="payment-14x" />
                      <Label htmlFor="payment-14x" className="flex items-center cursor-pointer">
                        <Calendar className="h-4 w-4 mr-1" />
                        <span>{t('income.payment14x')}</span>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="12x" id="payment-12x" />
                      <Label htmlFor="payment-12x" className="flex items-center cursor-pointer">
                        <Calendar className="h-4 w-4 mr-1" />
                        <span>{t('income.payment12x')}</span>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
                
                <p className="text-xs text-muted-foreground mt-1">{t('income.netSalaryDescription')}</p>
              </div>
              
              {/* Other Stable Income */}
              <div className="grid gap-2">
                <div className="flex justify-between">
                  <Label htmlFor="otherIncome">{t('income.otherIncomeLabel')}</Label>
                  <div className="text-right">
                    <span className="text-sm font-medium">€ {formatCurrency(otherIncome)}</span>
                  </div>
                </div>
                <Input
                  id="otherIncome"
                  type="number"
                  min={0}
                  value={otherIncome === 0 ? '' : otherIncome}
                  onChange={handleOtherIncomeChange}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">{t('income.otherIncomeDescription')}</p>
              </div>
              
              {/* Total Savings */}
              <div className="grid gap-2">
                <div className="flex justify-between">
                  <Label htmlFor="savings">{t('income.savingsLabel')}</Label>
                  <div className="text-right">
                    <span className="text-sm font-medium">€ {formatCurrency(savings)}</span>
                  </div>
                </div>
                <Input
                  id="savings"
                  type="number"
                  min={0}
                  value={savings === 0 ? '' : savings}
                  onChange={handleSavingsChange}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">{t('income.savingsDescription')}</p>
              </div>
              
              {/* Major Monthly Expenses */}
              <div className="grid gap-2">
                <div className="flex justify-between">
                  <Label htmlFor="majorExpenses">{t('income.expensesLabel')}</Label>
                  <div className="text-right">
                    <span className="text-sm font-medium">€ {formatCurrency(majorExpenses)}</span>
                  </div>
                </div>
                <Input
                  id="majorExpenses"
                  type="number"
                  min={0}
                  value={majorExpenses === 0 ? '' : majorExpenses}
                  onChange={handleMajorExpensesChange}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  {t('income.expensesDescription')}
                </p>
              </div>
              
              <Separator className="my-4" />
              
              {/* Results Section */}
       
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="max-w-3xl mx-auto mt-8">
        <Card>
          <CardHeader className="border-b border-[#ccd77b]">
            <CardTitle className="text-[#006b69] font-semibold text-3xl">{t('contact.title') || 'Contact Us'}</CardTitle>
           
          </CardHeader>
          <CardContent className="pt-6">
            <form
              action="https://submit-form.com/d2jn32phM"
              method="POST"
              className="space-y-4"
            >
              <input
                type="hidden"
                name="_redirect"
                value={`${window.location.origin}/thanks?email={email}&result=${encodeURIComponent(JSON.stringify({
                  eligibleIncome,
                  requiredIncome,
                  isEligible,
                  applicationType,
                  childrenCount,
                  netSalary,
                  otherIncome,
                  savings,
                  majorExpenses,
                  paymentType,
                  expenseDeduction: EXPENSE_DEDUCTION
                }))}`}
              />

              <div className="grid gap-2">
                <Label htmlFor="email" className='text-lg font-semibold'>{t('contact.emailLabel') || 'Your Email'}</Label>
                <Input
                  id="email"
                  type="email"
                  name="email"
                  placeholder={t('contact.emailPlaceholder') || 'Enter your email address'}
                  className="w-full text-lg mb-2"
                  required
                />
              </div>
              
              <Button 
                type="submit"
                className="w-full bg-[#006b69]hover:bg-[#005453] text-white"
              >
                {t('contact.submitButton') || 'Send Message'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
        <InfoCard
          title={t('infoCards.requirements.title')}
          description={t('infoCards.requirements.description')}
          items={[
            t('infoCards.requirements.items.0'),
            t('infoCards.requirements.items.1'),
            t('infoCards.requirements.items.2'),
            t('infoCards.requirements.items.3')
          ]}
        />
        
        <InfoCard
          title={t('infoCards.benefits.title')}
          description={t('infoCards.benefits.description')}
          items={[
            t('infoCards.benefits.items.0'),
            t('infoCards.benefits.items.1'),
            t('infoCards.benefits.items.2'),
            t('infoCards.benefits.items.3')
          ]}
        />
      </div>
      
 

  

      

      <div className="max-w-3xl md:max-w-fit mx-auto mt-8 p-4 bg-[#f5f7e5] rounded-lg flex justify-center">
        <a href="https://cta-eu1.hubspot.com/web-interactives/public/v1/track/redirect?encryptedPayload=AVxigLLQp7xCJc%2BNPkxU62hUiyUC%2FYMrvESSycR6Y7y0TW11LbGWcdqBRsXSJ%2FimkipVnVkh%2Bs0SxdtBbgyXKC0%2Ft6XO2NyHcfiMflMj4No6i8fmBKn7dAe71LuZyp5CQZYIx04IKICiwKHnhbTigD7YZWjPn05x5tE7%2FypFFHtZQCTZMNwplA%3D%3D&webInteractiveContentId=234541574356&portalId=139791174"
           target="_blank" rel="noopener">
          <img
              alt={t('footer.ctaAlt')}
              loading="lazy"
              src="https://hubspot-no-cache-eu1-prod.s3.amazonaws.com/cta/default/139791174/interactive-234541574356.png"
              className="border-b border-black object-contain"
          />
        </a>
      </div>
      <div className="text-center text-sm text-muted-foreground max-w-3xl mx-auto mt-8 p-4 bg-[#f5f7e5] rounded-lg">
        <p className="caption">{t('footer.lastUpdated')} <a href="https://mypaperwork.ai/" target="_blank" rel="noopener noreferrer">mypaperwork.ai</a></p>
        <p className="mt-1 quote text-sm">{t('footer.disclaimer')}</p>
      </div>
    </div>
  );
}

export default IncomeCalculator;
